import { useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight, BookOpen, CheckCircle2, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import type { WritingExpressionCategoryId } from '@examflow/exams';
import { Button } from '@examflow/ui';

export type WritingGradeView = {
  score: number;
  scoreMax: number;
  dimensionMax: number;
  scaleLabel: string;
  levelHint: string;
  feedback: string;
  strengths: string[];
  improvements: string[];
  dimensions: Array<{
    id: string;
    label: string;
    score: number;
    action: string;
  }>;
  /** Local heuristic fallback — show as 参考分, not formal AI grade. */
  isHeuristic: boolean;
};

type Dimension = WritingGradeView['dimensions'][number];

export type InlineWritingExpression = {
  id: string;
  text: string;
  gloss?: string;
};

export type WritingGradePanelProps = {
  grade: WritingGradeView;
  weakestDimension: Dimension | null;
  scoreDelta: number | null;
  firstDraft: string | null;
  firstGrade: WritingGradeView | null;
  currentDraft: string;
  hasCompletedRevision: boolean;
  revisionCredit: string | null;
  /** Learner is editing against this grade — keep result, default collapsed. */
  revisionMode?: boolean;
  /** 2–3 static bank phrases for the weak dimension (click → insert + revise). */
  inlineExpressions?: InlineWritingExpression[];
  onInsertExpression?: (text: string) => void;
  /** Opens the full expression aid focused on this category（「查看更多」）. */
  expressionBrowseCategory?: WritingExpressionCategoryId | null;
  onBrowseExpressions?: (category: WritingExpressionCategoryId) => void;
  onStartRevision: () => void;
  onComplete: () => void;
};

function dimensionFillPercent(score: number, max: number): number {
  if (max <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((score / max) * 100)));
}

/**
 * Writing AI grade result chrome — decision-first: score → weak dim → revise / finish.
 * Grade stays in memory while revising; collapse/expand is local UI only (no re-grade).
 */
export function WritingGradePanel({
  grade,
  weakestDimension,
  scoreDelta,
  firstDraft,
  firstGrade,
  currentDraft,
  hasCompletedRevision,
  revisionCredit,
  revisionMode = false,
  inlineExpressions = [],
  onInsertExpression,
  expressionBrowseCategory = null,
  onBrowseExpressions,
  onStartRevision,
  onComplete,
}: WritingGradePanelProps) {
  const [expanded, setExpanded] = useState(!revisionMode);
  const showCompare =
    Boolean(firstDraft) && firstDraft !== currentDraft.trim() && firstGrade != null;
  const showInlineExpressions =
    inlineExpressions.length > 0 && typeof onInsertExpression === 'function';
  const showExpressionBrowse =
    Boolean(expressionBrowseCategory) && typeof onBrowseExpressions === 'function';

  useEffect(() => {
    // Entering revision: tuck the panel so the editor has room; expand still shows same grade.
    setExpanded(!revisionMode);
  }, [revisionMode]);

  return (
    <section
      className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
      data-testid="writing-grade-panel"
      aria-label="改卷评分结果"
      data-revision-mode={revisionMode ? 'true' : 'false'}
      data-expanded={expanded ? 'true' : 'false'}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3 sm:px-6">
        <div className="min-w-0 flex flex-wrap items-center gap-2.5">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
            <Sparkles className="h-3.5 w-3.5 text-primary-600" aria-hidden />
            {grade.isHeuristic ? '参考评分' : 'AI 改卷'}
          </span>
          <span className="font-mono text-lg font-black tabular-nums text-slate-950">
            {grade.score}
            <span className="text-sm font-semibold text-slate-400"> / {grade.scoreMax}</span>
          </span>
          {revisionMode ? (
            <span className="rounded-md bg-primary-50 px-2 py-0.5 text-xs font-semibold text-primary-800">
              修订中 · 结果仍保留
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          aria-expanded={expanded}
          data-testid="writing-grade-panel-toggle"
        >
          {expanded ? '收起详情' : '展开详情'}
          {expanded ? (
            <ChevronUp className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
      </div>

      {!expanded ? (
        <div className="px-5 py-3 text-sm text-slate-600 sm:px-6" data-testid="writing-grade-panel-collapsed">
          {weakestDimension
            ? `优先改「${weakestDimension.label}」· ${grade.levelHint}`
            : grade.levelHint}
          {revisionMode ? ' · 展开可对照改法，无需重新判卷' : ''}
        </div>
      ) : null}

      {expanded ? (
        <>
          {grade.isHeuristic ? (
            <div
              className="flex items-start gap-2.5 border-b border-amber-200/80 bg-amber-50 px-5 py-3 text-sm text-amber-950"
              data-testid="writing-heuristic-grade-banner"
              role="status"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
              <p>
                当前为<strong className="font-semibold">参考分</strong>
                （AI 阅卷暂不可用）。不计入能力诊断；恢复后请再评一次。
              </p>
            </div>
          ) : null}

          <div className="border-b border-slate-100 px-5 py-5 sm:px-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {grade.isHeuristic ? (
                    <span className="rounded-md bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">
                      参考分
                    </span>
                  ) : null}
                </div>
                <div className="mt-1 flex flex-wrap items-end gap-x-3 gap-y-1">
                  <span className="font-mono text-4xl font-black tabular-nums tracking-tight text-slate-950 sm:text-5xl">
                    {grade.score}
                  </span>
                  <span className="mb-1 font-mono text-lg tabular-nums text-slate-400">
                    / {grade.scoreMax}
                  </span>
                  {scoreDelta != null ? (
                    <span
                      className={`mb-1.5 rounded-md px-2 py-0.5 text-sm font-semibold ${
                        scoreDelta > 0
                          ? 'bg-emerald-50 text-emerald-800'
                          : scoreDelta < 0
                            ? 'bg-warning-50 text-warning-800'
                            : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      较初稿 {scoreDelta > 0 ? '+' : ''}
                      {scoreDelta}
                    </span>
                  ) : null}
                </div>
                <p className="mt-2 text-sm text-slate-500">{grade.scaleLabel}</p>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-950 px-4 py-3 text-white">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                  水平提示
                </div>
                <div className="mt-1 max-w-[12rem] text-base font-semibold leading-snug">
                  {grade.levelHint}
                </div>
              </div>
            </div>

            <p className="mt-5 text-[15px] leading-7 text-slate-800">{grade.feedback}</p>
          </div>

          {grade.dimensions.length > 0 ? (
            <div className="border-b border-slate-100 px-5 py-5 sm:px-6">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold text-slate-950">分项表现</h3>
                <span className="text-xs text-slate-500">各 0–{grade.dimensionMax}</span>
              </div>
              <ul className="mt-4 space-y-3">
                {grade.dimensions.map((dim) => {
                  const isWeakest = weakestDimension?.id === dim.id;
                  const fill = dimensionFillPercent(dim.score, grade.dimensionMax);
                  return (
                    <li
                      key={dim.id}
                      className={
                        isWeakest
                          ? 'rounded-lg border border-warning-200 bg-warning-50/70 p-3.5'
                          : 'rounded-lg border border-slate-100 bg-slate-50/80 p-3.5'
                      }
                      data-testid={isWeakest ? 'writing-weakest-dimension' : undefined}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold text-slate-900">{dim.label}</span>
                            {isWeakest ? (
                              <span className="rounded bg-warning-100 px-1.5 py-0.5 text-[11px] font-bold text-warning-800">
                                本篇优先改
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <span className="shrink-0 font-mono text-sm font-semibold tabular-nums text-slate-800">
                          {dim.score}
                          <span className="font-normal text-slate-400">/{grade.dimensionMax}</span>
                        </span>
                      </div>
                      <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-slate-200/90">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            isWeakest ? 'bg-warning-500' : 'bg-slate-800'
                          }`}
                          style={{ width: `${fill}%` }}
                        />
                      </div>
                      <p className="mt-2 text-sm leading-6 text-slate-600">
                        <span className="font-medium text-slate-700">下次怎么改：</span>
                        {dim.action}
                      </p>
                      {isWeakest && showInlineExpressions ? (
                        <div
                          className="mt-3 rounded-md border border-warning-200/80 bg-white/70 px-3 py-2.5"
                          data-testid="writing-inline-expressions"
                        >
                          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                            可插入的表达
                          </div>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {inlineExpressions.map((item) => (
                              <button
                                key={item.id}
                                type="button"
                                title={item.gloss ?? item.text}
                                onClick={() => onInsertExpression?.(item.text)}
                                className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-left text-sm font-medium text-slate-900 hover:border-primary-300 hover:bg-primary-50"
                                data-testid={`writing-inline-expression-${item.id}`}
                              >
                                {item.text}
                              </button>
                            ))}
                          </div>
                          {showExpressionBrowse && expressionBrowseCategory ? (
                            <button
                              type="button"
                              onClick={() => onBrowseExpressions?.(expressionBrowseCategory)}
                              className="mt-2 text-xs font-medium text-primary-700 hover:text-primary-800 hover:underline"
                              data-testid="writing-browse-expressions"
                            >
                              查看更多
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          {(grade.strengths.length > 0 || grade.improvements.length > 0) && (
            <div className="grid gap-0 border-b border-slate-100 sm:grid-cols-2">
              {grade.strengths.length > 0 ? (
                <div className="border-b border-slate-100 px-5 py-4 sm:border-b-0 sm:border-r sm:px-6">
                  <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
                    写得好的地方
                  </h3>
                  <ul className="mt-3 space-y-2">
                    {grade.strengths.map((item) => (
                      <li key={item} className="flex gap-2 text-sm leading-6 text-slate-700">
                        <CheckCircle2
                          className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600"
                          aria-hidden
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {grade.improvements.length > 0 ? (
                <div className="px-5 py-4 sm:px-6">
                  <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
                    优先改这 {grade.improvements.length} 点
                  </h3>
                  <ol className="mt-3 list-decimal space-y-2 pl-4 text-sm leading-6 text-slate-800">
                    {grade.improvements.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ol>
                </div>
              ) : null}
            </div>
          )}

          {showCompare && firstDraft && firstGrade ? (
            <div className="border-b border-slate-100 px-5 py-5 sm:px-6">
              <h3 className="text-sm font-semibold text-slate-950">初稿 · 修订对照</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3.5">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-slate-500">初稿</span>
                    <span className="font-mono text-xs tabular-nums text-slate-700">
                      {firstGrade.score}/{firstGrade.scoreMax}
                      {firstGrade.isHeuristic ? ' · 参考' : ''}
                    </span>
                  </div>
                  <p className="line-clamp-5 whitespace-pre-wrap text-xs leading-5 text-slate-600">
                    {firstDraft}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-3.5 ring-1 ring-slate-900/5">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-slate-500">修订稿</span>
                    <span className="font-mono text-xs tabular-nums text-slate-700">
                      {grade.score}/{grade.scoreMax}
                      {grade.isHeuristic ? ' · 参考' : ''}
                    </span>
                  </div>
                  <p className="line-clamp-5 whitespace-pre-wrap text-xs leading-5 text-slate-600">
                    {currentDraft.trim()}
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          {revisionMode ? (
            <div
              className="border-t border-slate-200 bg-slate-50/80 px-5 py-3 text-sm text-slate-600 sm:px-6"
              data-testid="writing-grade-revision-hint"
            >
              对照改法修订正文；改完后在编辑区点「再评修订稿」。展开/收起不会重新判卷。
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2.5 border-t border-slate-200 bg-white px-5 py-4 sm:px-6">
              {!hasCompletedRevision ? (
                <Button
                  type="button"
                  size="md"
                  onClick={onStartRevision}
                  rightIcon={<ArrowRight className="h-4 w-4" aria-hidden />}
                  className="shadow-sm"
                >
                  {weakestDimension
                    ? `针对「${weakestDimension.label}」再写一版`
                    : '再写一版'}
                  {revisionCredit ? (
                    <span className="rounded bg-white/15 px-1.5 py-0.5 text-[11px] font-semibold tracking-wide">
                      再评免费
                    </span>
                  ) : null}
                </Button>
              ) : null}
              {showExpressionBrowse && expressionBrowseCategory && !showInlineExpressions ? (
                <Button
                  type="button"
                  size="md"
                  variant="outline"
                  onClick={() => onBrowseExpressions?.(expressionBrowseCategory)}
                  leftIcon={<BookOpen className="h-4 w-4" aria-hidden />}
                  data-testid="writing-browse-expressions"
                >
                  查看更多
                </Button>
              ) : null}
              <Button
                type="button"
                size="md"
                variant={hasCompletedRevision ? 'primary' : 'outline'}
                onClick={onComplete}
                className={hasCompletedRevision ? 'shadow-sm' : undefined}
              >
                完成本次
              </Button>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}
